import { Box, Button, Grid, Typography, useTheme } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import {
  BCPArrowUpArrowDown,
  BCPDiceFive,
  BCPFilter,
  BCPMap,
  BCPSearch,
  BCPXmark
} from "../../../components/icons/bcp/BCPIcons";
import EventCard from "../../../components/layout/EventCard";
import InfoContainer from "../../../components/layout/InfoContainer";
import OptionsMenu from "../../../components/layout/OptionsMenu";
import ListMap from "../../../components/other/ListMap";
import ListResponsive from "../../../components/other/ListResponsive";
import { AuthContext } from "../../../components/user/BCPAuthProvider";
import {
  clearFilterSx,
  filterAndSortEvents,
  getFilterSx,
  updateFilters
} from "../../../components/utilities/SortFilterFunctions";
import { isAppMode } from "../../../global/AppOverlay";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import { ActiveEventsModal } from "../../organize/EventsOrganize/EventsOrganizeDefault";
import { useTranslation } from "react-i18next";

export default function PlayDashEvents(props) {
  const {
    events,
    loading,
    gameSystems,
    title,
    HeaderButton,
    NoResultsPanel,
    zoom
  } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.dashboards.play"
  });

  const authContext = useContext(AuthContext);
  const appMode = isAppMode();
  const theme = useTheme();

  const [filteredEvents, setFilteredEvents] = useState(null);
  const [filters, setFilters] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [anchorEl3, setAnchorEl3] = useState(null);

  const [toggleMap, setToggleMap] = useState(true);
  const [activeEvents, setActiveEvents] = useState(null);

  // Filter and sort events
  useEffect(() => {
    if (events?.length && filters.length) {
      const tempEvents = filterAndSortEvents(events, filters);

      setFilteredEvents(tempEvents);
    } else if (!filters.length) {
      setFilteredEvents(null);
    }
    // eslint-disable-next-line
  }, [filters, events]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={8}>
        <Typography variant="h3" fontWeight="700">
          {title || "Events"}
        </Typography>
      </Grid>

      {HeaderButton && (
        <Grid item xs={12} md={4}>
          {HeaderButton}
        </Grid>
      )}

      <Grid item xs={12} mt={1}>
        {!loading && !events?.length ? (
          <>
            {NoResultsPanel ? (
              <>{NoResultsPanel}</>
            ) : (
              <InfoContainer
                title={t("noEventsFoundTitle")}
                body={t("noEventsFoundDescription")}
                ActionButton={
                  <Button
                    variant="contained"
                    href="/play/events"
                    target={`${appMode ? "_blank" : "_self"}`}
                    startIcon={<BCPSearch />}
                  >
                    {t("searchForEvents")}
                  </Button>
                }
              />
            )}
          </>
        ) : (
          <ListResponsive
            name="Find Events"
            originalList={
              loading
                ? null
                : filteredEvents !== null
                  ? filteredEvents
                  : events
                    ? events
                    : []
            }
            doPages
            initialRowsPerPage={3}
            itemsPerPage={[3]}
            centerNav
            doSearch
            actionButtonBottom={[
              {
                label: t("sort", {
                  keyPrefix: "buttons"
                }),
                startIcon: <BCPArrowUpArrowDown />,
                onClick: (e) => {
                  setAnchorEl(e.currentTarget);
                },
                disabled: loading || !events?.length,
                sx: {
                  ...getFilterSx(
                    Boolean(
                      filters.length &&
                        filters.some((item) => item.includes("sort"))
                    ),
                    filters,
                    theme
                  )
                }
              },
              {
                label: t("filters", {
                  keyPrefix: "buttons"
                }),
                startIcon: <BCPFilter />,
                onClick: (e) => {
                  setAnchorEl2(e.currentTarget);
                },
                disabled: loading || !events?.length,
                sx: {
                  ...getFilterSx(
                    Boolean(
                      filters.length &&
                        filters.some((item) => item.includes("filter"))
                    ),
                    filters,
                    theme
                  )
                }
              },
              getEnvironmentVariable("SINGLE_GAME_SYSTEM") !== "true" &&
                getEnvironmentVariable("SINGLE_GAME_MANUFACTURER") !==
                  "true" && {
                  label: t("gameSystems", {
                    keyPrefix: "buttons"
                  }),
                  startIcon: <BCPDiceFive />,
                  disabled: loading || !events?.length || !gameSystems?.length,
                  onClick: (e) => {
                    setAnchorEl3(e.currentTarget);
                  },
                  sx: {
                    ...getFilterSx(
                      Boolean(
                        filters.length &&
                          filters.some((item) => item.includes("gamesystem"))
                      ),
                      filters,
                      theme
                    )
                  }
                },
              {
                label: t("toggleMap", {
                  keyPrefix: "buttons"
                }),
                startIcon: <BCPMap />,
                onClick: (e) => {
                  setToggleMap(!toggleMap);
                },
                disabled: loading || !events?.length,
                sx: {
                  ...getFilterSx(!loading && toggleMap, filters, theme)
                }
              },
              {
                label: t("clearFilters", {
                  keyPrefix: "buttons"
                }),
                startIcon: <BCPXmark />,

                disabled: loading || !events?.length,
                sx: { ...clearFilterSx(false, filters, theme) },
                onClick: () => {
                  updateFilters(null, null, null, setFilters);
                  setFilteredEvents(null);
                  setToggleMap(true);
                  setActiveEvents(null);
                  setAnchorEl(null);
                  setAnchorEl2(null);
                  setAnchorEl3(null);
                }
              }
            ]}
            searchProps={{
              label: t("searchEvents"),
              performSearch: (originalList, searchText) => {
                if (!searchText || searchText === "") {
                  return originalList;
                } else {
                  var filteredList = originalList.filter(function (el) {
                    if (el?.eventLocation?.toLowerCase().includes(searchText))
                      return true;
                    if (el?.name?.toLowerCase().includes(searchText))
                      return true;
                    if (
                      el?.gameSystem?.name?.toLowerCase().includes(searchText)
                    )
                      return true;
                    return false;
                  });
                  return filteredList;
                }
              }
            }}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                  <EventCard
                    event={row}
                    key={row?.id || index}
                    authContext={authContext}
                    showStatus
                    preventImageFetch
                  />
                </Grid>
              )
            }}
          >
            {toggleMap && !loading && (
              <Box sx={{ mt: [0, 2] }}>
                <ListMap
                  name="events-map"
                  originalList={
                    filteredEvents !== null
                      ? filteredEvents.filter((event) => !event?.isOnlineEvent)
                      : events
                        ? events.filter((event) => !event?.isOnlineEvent)
                        : null
                  }
                  zoom={zoom || 10}
                  sx={{ mb: 1 }}
                  mapSx={{ height: "30vh" }}
                  defaultCenter={
                    filteredEvents?.length
                      ? {
                          lat: filteredEvents?.[0]?.coordinate?.[1],
                          lng: filteredEvents?.[0]?.coordinate?.[0]
                        }
                      : events?.length
                        ? {
                            lat: events?.[0]?.coordinate?.[1],
                            lng: events?.[0]?.coordinate?.[0]
                          }
                        : null
                  }
                  onClick={(lat, lng) => {
                    const eventArr = [];
                    events.forEach((event) => {
                      if (
                        event?.coordinate?.[0] === lng &&
                        event?.coordinate?.[1] === lat
                      ) {
                        eventArr.push(event);
                      }
                    });

                    setActiveEvents(eventArr);
                  }}
                  filterFunction={(event) =>
                    !event?.isOnlineEvent &&
                    (event?.coordinate?.length ||
                      event?.coordinate_point?.coordinates?.length) &&
                    (event?.coordinate[0] ||
                      event?.coordinate_point?.coordinates[0]) &&
                    (event?.coordinate[1] ||
                      event?.coordinate_point?.coordinates[1])
                  }
                />
              </Box>
            )}

            {/* Sorting */}
            <OptionsMenu
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              items={[
                {
                  name: "sort-date-asc",
                  label: t("startDateAscending", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("sort-date-asc"),
                  onClick: () =>
                    updateFilters("sort-date-asc", false, filters, setFilters)
                },
                {
                  name: "sort-date-desc",
                  label: t("startDateDescending", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("sort-date-desc"),
                  onClick: () =>
                    updateFilters("sort-date-desc", false, filters, setFilters)
                },
                {
                  name: "sort-name-asc",
                  label: t("nameAscending", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("sort-name-asc"),
                  onClick: () =>
                    updateFilters("sort-name-asc", false, filters, setFilters)
                },
                {
                  name: "sort-name-desc",
                  label: t("nameDescending", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("sort-name-desc"),
                  onClick: () =>
                    updateFilters("sort-name-desc", false, filters, setFilters)
                },
                {
                  name: "sort-rounds-asc",
                  label: t("roundsAscending", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("sort-rounds-asc"),
                  onClick: () =>
                    updateFilters("sort-rounds-asc", false, filters, setFilters)
                },
                {
                  name: "sort-rounds-desc",
                  label: t("roundsDescending", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("sort-rounds-desc"),
                  onClick: () =>
                    updateFilters(
                      "sort-rounds-desc",
                      false,
                      filters,
                      setFilters
                    )
                }
              ]}
              name="events-sort-menu"
            />

            {/* Filters */}
            <OptionsMenu
              anchorEl={anchorEl2}
              setAnchorEl={setAnchorEl2}
              items={[
                {
                  name: "filter-online",
                  label: t("onlineEvents", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("filter-online"),
                  onClick: () =>
                    updateFilters(
                      "filter-online",
                      "filter-exclude-online",
                      filters,
                      setFilters
                    )
                },
                {
                  name: "filter-exclude-online",
                  label: t("excludeOnline", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("filter-exclude-online"),
                  onClick: () =>
                    updateFilters(
                      "filter-exclude-online",
                      "filter-online",
                      filters,
                      setFilters
                    )
                },
                { divider: true },

                {
                  name: "filter-org",
                  label: t("gameStoreEvents", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("filter-org"),
                  onClick: () =>
                    updateFilters(
                      "filter-org",
                      "filter-exclude-org",
                      filters,
                      setFilters
                    )
                },
                {
                  name: "filter-exclude-org",
                  label: t("excludeGameStoreEvents", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("filter-exclude-org"),
                  onClick: () =>
                    updateFilters(
                      "filter-exclude-org",
                      "filter-org",
                      filters,
                      setFilters
                    )
                },
                { divider: true },

                {
                  name: "filter-singles",
                  label: t("singlesEvents", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("filter-singles"),
                  onClick: () =>
                    updateFilters(
                      "filter-singles",
                      ["filter-doubles", "filter-teams", "filter-board"],
                      filters,
                      setFilters
                    )
                },
                {
                  name: "filter-doubles",
                  label: t("doublesEvents", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("filter-doubles"),
                  onClick: () =>
                    updateFilters(
                      "filter-doubles",
                      ["filter-singles", "filter-teams", "filter-board"],
                      filters,
                      setFilters
                    )
                },
                {
                  name: "filter-teams",
                  label: t("teamsEvents", {
                    keyPrefix: "fields"
                  }),
                  selected: filters.includes("filter-teams"),
                  onClick: () =>
                    updateFilters(
                      "filter-teams",
                      ["filter-singles", "filter-doubles", "filter-board"],
                      filters,
                      setFilters
                    )
                }
              ]}
              name="events-filter-menu"
            />

            {/* Game Systems */}
            {gameSystems?.length && (
              <OptionsMenu
                anchorEl={anchorEl3}
                setAnchorEl={setAnchorEl3}
                items={gameSystems.map((gameSystem) => ({
                  name: `gamesystem-${gameSystem?.id}`,
                  label: gameSystem.name,
                  selected: filters.includes(`gamesystem-${gameSystem?.id}`),
                  onClick: () =>
                    updateFilters(
                      `gamesystem-${gameSystem?.id}`,
                      false,
                      filters,
                      setFilters
                    )
                }))}
                name="gamesystem-menu"
              />
            )}

            {activeEvents?.length && (
              <ActiveEventsModal
                activeEvents={activeEvents}
                setActiveEvents={setActiveEvents}
              />
            )}
          </ListResponsive>
        )}
      </Grid>
    </Grid>
  );
}
